/**
 * Deterministic tag → palette-slot mapping for theme tag hue rotation.
 *
 * Themes may declare a small tag palette (`tag-1-*` … `tag-3-*` tokens);
 * every client maps each tag to one of those slots with THIS function so the
 * same tag renders in the same color on every device and platform.
 *
 * Mirrored 1:1 in Swift (`TagPalette.swift` in CaliuCore) — change both or
 * neither.
 */

/** Number of tag palette slots every theme resolves (see generate-tokens.mjs). */
export const TAG_PALETTE_SIZE = 3

/**
 * FNV-1a 32-bit hash. Chosen because it is trivial to mirror bit-for-bit in
 * Swift (UInt32 arithmetic, no lookup tables) and stable across platforms.
 */
function fnv1a(input: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    // 32-bit FNV prime multiplication, kept in uint32 range
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash >>> 0
}

/**
 * Map a tag name to a palette slot index in [0, count).
 *
 * Hashes only the tag's TOP-LEVEL segment, lowercased, so `#work` and
 * `#work/project` always land on the same slot and a hierarchy reads as one
 * hue. Leading `#` is tolerated and stripped.
 */
export function tagPaletteIndex(
  tagName: string,
  count: number = TAG_PALETTE_SIZE,
): number {
  if (count <= 0) return 0
  const normalized = tagName.replace(/^#/, "").toLowerCase()
  const topLevel = normalized.split("/")[0] ?? ""
  return fnv1a(topLevel) % count
}
